import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, Home as HomeIcon, Building, Map, Store,
  X, ChevronDown, ArrowRight, ChevronLeft, ChevronRight,
  TrendingUp, CheckCircle, AlertCircle, Coffee, Car
} from 'lucide-react';
import PropertyCard from '../components/PropertyCard';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  }
};

// Componentes de estado
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
    <motion.div
      className="flex flex-col items-center gap-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <p className="text-indigo-700 dark:text-indigo-400 font-medium">Cargando propiedades...</p>
    </motion.div>
  </div>
);

const ErrorState = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 px-4">
    <motion.div
      className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-xl shadow-xl dark:shadow-black/50 p-8 max-w-md w-full border border-white/50 dark:border-gray-700/50"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center">
        <div className="bg-red-50 dark:bg-red-500/20 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg">
          <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Error al cargar las propiedades
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
        <motion.button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow-lg dark:shadow-black/25 transition-all duration-300"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          Reintentar
        </motion.button>
      </div>
    </motion.div>
  </div>
);

const NoResults = () => (
  <motion.div 
    className="flex flex-col items-center justify-center py-16 backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl shadow-xl dark:shadow-black/50 border border-white/50 dark:border-gray-700/50"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
  >
    <motion.div 
      className="bg-indigo-50 dark:bg-indigo-900/30 w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg"
      whileHover={{ rotate: 12, scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300, damping: 10 }}
    >
      <Search className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
    </motion.div>
    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">
      No se encontraron propiedades
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto text-center">
      No hay propiedades disponibles en esta categoría por el momento. Intenta con otros criterios de búsqueda.
    </p>
    <motion.button
      onClick={() => window.location.reload()}
      className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white rounded-xl shadow-lg dark:shadow-black/25 hover:shadow-xl dark:hover:shadow-black/40 transition-all duration-300"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <ArrowRight className="w-5 h-5" />
      <span>Buscar otras propiedades</span>
    </motion.button>
  </motion.div>
);

const typeMap = {
  'casas': { 
    apiType: 'casa',
    label: 'Casas',
    icon: HomeIcon
  },
  'departamentos': { 
    apiType: 'departamento',
    label: 'Departamentos',
    icon: Building
  },
  'terrenos': { 
    apiType: 'terreno',
    label: 'Terrenos',
    icon: Map
  },
  'locales': { 
    apiType: 'local',
    label: 'Locales Comerciales',
    icon: Store
  },
  'oficinas': { 
    apiType: 'oficina',
    label: 'Oficinas',
    icon: Coffee
  },
  'cocheras': { 
    apiType: 'cochera',
    label: 'Cocheras',
    icon: Car
  }
};

// Pagination button component
const PaginationButton = ({ onClick, active, children }) => (
  <motion.button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg transition-all duration-300 ${
      active 
      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25' 
      : 'backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 border border-gray-200/50 dark:border-gray-700/50'
    }`}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    {children}
  </motion.button>
);

const PropertyTypePage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [propiedades, setPropiedades] = useState([]);
  const [searchTitle, setSearchTitle] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    operacion: 'venta',
    precioMin: '',
    precioMax: '',
  });
  // Fijamos exactamente 12 propiedades por página (3 columnas x 4 filas)
  const propertiesPerPage = 12;
  const [debouncedSearchTitle, setDebouncedSearchTitle] = useState('');
  
  // References to property type icons
  const getTypeIcon = () => {
    if (!type || !typeMap[type]) return HomeIcon;
    return typeMap[type].icon;
  };

  const TypeIcon = getTypeIcon();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTitle(searchTitle);
    }, 500);
  
    return () => clearTimeout(timer);
  }, [searchTitle]);
  
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        if (!type || !typeMap[type]) {
          console.error('Invalid property type:', type);
          navigate('/propiedades/casas');
          return;
        }
  
        const currentType = typeMap[type].apiType;
        console.log('Current type:', currentType);
        
        const searchParams = new URLSearchParams({
          tipo: currentType
        });
  
        const url = `https://backend-production-1786.up.railway.app/api/propiedades?${searchParams.toString()}`;
        console.log('Fetching URL:', url);
  
        const response = await fetch(url);
        const data = await response.json();
        
        let filteredData = data;
        
        if (Array.isArray(data) && data.length > 0) {
          filteredData = data.filter(prop => {
            return filters.operacion === 'alquiler' ? 
              prop.operacion?.toLowerCase() === 'alquiler' : 
              (prop.operacion?.toLowerCase() === 'venta' || prop.operacion?.toLowerCase() === 'comprar');
          });
        }
  
        if (filters.precioMin) {
          filteredData = filteredData.filter(prop => {
            const precio = filters.operacion === 'alquiler' 
              ? parseFloat(prop.precio_mensual || 0) 
              : parseFloat(prop.precio_soles || 0);
            return precio >= parseFloat(filters.precioMin);
          });
        }
        
        if (filters.precioMax) {
          filteredData = filteredData.filter(prop => {
            const precio = filters.operacion === 'alquiler' 
              ? parseFloat(prop.precio_mensual || 0) 
              : parseFloat(prop.precio_soles || 0);
            return precio <= parseFloat(filters.precioMax);
          });
        }
  
        if (debouncedSearchTitle.trim()) {
          filteredData = filteredData.filter(prop => 
            prop.titulo.toLowerCase().includes(debouncedSearchTitle.toLowerCase().trim())
          );
        }
  
        setPropiedades(filteredData);
  
      } catch (error) {
        console.error('Error fetching properties:', error);
        setError('Error al cargar las propiedades');
        setPropiedades([]);
      } finally {
        setLoading(false);
      }
    };
  
    setLoading(true);
    fetchProperties();
  }, [type, filters.operacion, filters.precioMin, filters.precioMax, debouncedSearchTitle, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const currentType = typeMap[type]?.apiType;
      
      // Usar el mismo endpoint que en el useEffect
      const searchParams = new URLSearchParams({
        tipo: currentType
      });
  
      const url = `https://backend-production-1786.up.railway.app/api/propiedades?${searchParams.toString()}`;
  
      const response = await fetch(url);
      const data = await response.json();
  
      // Aplicar filtros localmente
      let filteredData = data;
      
      if (filters.precioMin) {
        filteredData = filteredData.filter(prop => 
          parseFloat(prop.precio_soles) >= parseFloat(filters.precioMin)
        );
      }
      
      if (filters.precioMax) {
        filteredData = filteredData.filter(prop => 
          parseFloat(prop.precio_soles) <= parseFloat(filters.precioMax)
        );
      }
  
      if (searchTitle.trim()) {
        filteredData = filteredData.filter(prop => 
          prop.titulo.toLowerCase().includes(searchTitle.toLowerCase().trim())
        );
      }
  
      setPropiedades(filteredData);
      // Resetear a la primera página al aplicar nuevos filtros
      setCurrentPage(1);
  
    } catch (error) {
      console.error('Error in search:', error);
      setError('Error al buscar propiedades');
      setPropiedades([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrar propiedades por título de búsqueda
  const filteredProperties = propiedades.filter(property =>
    property.titulo.toLowerCase().includes((searchTitle || '').toLowerCase())
  );
  
  // Calcular paginación
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  
  // Función para generar el array de números de página para paginación
  const getPageNumbers = () => {
    const pageNumbers = [];
    const MAX_VISIBLE_PAGES = 5;
    
    if (totalPages <= MAX_VISIBLE_PAGES) {
      // Si hay 5 o menos páginas, mostrar todas
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Lógica para mostrar paginación cuando hay muchas páginas
      if (currentPage <= 3) {
        // Cerca del inicio
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        // Cerca del final
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // En medio
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pageNumbers.push(i);
        }
      }
    }
    
    return pageNumbers;
  };
  
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section with Search - styled like Home.jsx hero */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-900 dark:from-gray-800 dark:via-gray-900 dark:to-black overflow-hidden">
          {/* Animated Orbs */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 dark:from-blue-500/10 dark:to-indigo-500/10 blur-2xl"
              style={{
                width: `${Math.random() * 300 + 100}px`,
                height: `${Math.random() * 300 + 100}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
              animate={{
                x: [0, Math.random() * 100 - 50, 0],
                y: [0, Math.random() * 100 - 50, 0],
                scale: [1, Math.random() * 0.5 + 1, 1],
              }}
              transition={{
                duration: Math.random() * 10 + 15,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          
          {/* Mesh Grid Overlay */}
          <div 
            className="absolute inset-0 opacity-10 dark:opacity-5"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}
          />
        </div>
  
        <div className="relative py-16">
          <motion.div 
            className="max-w-7xl mx-auto px-4 sm:px-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="flex justify-center mb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm p-3 rounded-full border border-white/20 dark:border-white/10">
                  <TypeIcon className="w-8 h-8 text-white" />
                </div>
              </motion.div>
              
              <motion.h1
                className="text-4xl md:text-5xl font-bold text-white mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {typeMap[type]?.label || 'Propiedades'}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-200 dark:from-blue-400 dark:to-indigo-300">
                  En {filters.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
                </span>
              </motion.h1>
              
              <motion.p
                className="text-xl text-white/80 dark:text-white/70 mb-8 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                Encuentra {typeMap[type]?.label.toLowerCase() || 'propiedades'} que se adaptan a tu estilo de vida y presupuesto
              </motion.p>
            </motion.div>
  
            {/* Search Container */}
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              {/* Tabs - Operation Type */}
              <div className="mb-4 p-1 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-xl inline-flex mx-auto border border-white/20 dark:border-white/10">
                <motion.button
                  onClick={() => setFilters(prev => ({ ...prev, operacion: 'venta' }))}
                  className={`group px-6 py-2 rounded-lg transition-all duration-300 ${
                    filters.operacion === 'venta' 
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-md dark:shadow-black/25' 
                      : 'text-white hover:bg-white/20 dark:hover:bg-white/10'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Comprar
                </motion.button>
                <motion.button
                  onClick={() => setFilters(prev => ({ ...prev, operacion: 'alquiler' }))}
                  className={`group px-6 py-2 rounded-lg transition-all duration-300 ${
                    filters.operacion === 'alquiler' 
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-md dark:shadow-black/25' 
                      : 'text-white hover:bg-white/20 dark:hover:bg-white/10'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Alquilar
                </motion.button>
              </div>
  
              {/* Search Form */}
              <div className="bg-white/10 dark:bg-white/5 backdrop-blur-md p-4 rounded-2xl shadow-xl dark:shadow-black/50 border border-white/20 dark:border-white/10">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Buscar propiedades por título..."
                        value={searchTitle}
                        onChange={(e) => setSearchTitle(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 dark:bg-white/5 text-white placeholder-white/60 dark:placeholder-white/50
                                border border-white/20 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 dark:focus:ring-white/20
                                transition-all duration-300"
                      />
                    </div>
  
                    <div className="flex gap-3">
                      <motion.button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`group px-6 py-4 rounded-xl transition-all duration-300 ${
                          showFilters 
                            ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400' 
                            : 'bg-white/10 dark:bg-white/5 text-white'
                        } flex items-center gap-2 border border-white/20 dark:border-white/10`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Filter className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" />
                        <span>Filtros</span>
                      </motion.button>
  
                      <motion.button
                        type="submit"
                        className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white 
                                rounded-xl shadow-md dark:shadow-black/25 flex items-center gap-2"
                        whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)" }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Search className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                        <span>Buscar</span>
                      </motion.button>
                    </div>
                  </div>
  
                  {/* Filters Section */}
                  <AnimatePresence>
                    {showFilters && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="p-5 bg-white/10 dark:bg-white/5 backdrop-blur-md rounded-xl border border-white/20 dark:border-white/10">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-white mb-2">
                                {filters.operacion === 'alquiler' ? 'Precio mensual mínimo (S/)' : 'Precio mínimo (S/)'}
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={filters.precioMin}
                                  onChange={(e) => setFilters({...filters, precioMin: e.target.value})}
                                  placeholder="Mínimo"
                                  className="w-full px-4 py-3 rounded-xl bg-white/10 dark:bg-white/5 text-white placeholder-white/60 dark:placeholder-white/50
                                          border border-white/20 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 dark:focus:ring-white/20"
                                />
                              </div>
                            </div>
  
                            <div>
                              <label className="block text-sm font-medium text-white mb-2">
                                {filters.operacion === 'alquiler' ? 'Precio mensual máximo (S/)' : 'Precio máximo (S/)'}
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={filters.precioMax}
                                  onChange={(e) => setFilters({...filters, precioMax: e.target.value})}
                                  placeholder="Máximo"
                                  className="w-full px-4 py-3 rounded-xl bg-white/10 dark:bg-white/5 text-white placeholder-white/60 dark:placeholder-white/50
                                          border border-white/20 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 dark:focus:ring-white/20"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-end mt-4">
                            <motion.button
                              type="button"
                              onClick={() => {
                                setFilters(prev => ({ ...prev, precioMin: '', precioMax: '' }));
                                setShowFilters(false);
                              }}
                              className="px-4 py-2 bg-white/10 dark:bg-white/5 text-white rounded-lg mr-3 border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <X className="w-5 h-5" />
                            </motion.button>
                            <motion.button
                              type="submit"
                              className="px-6 py-2 bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center gap-2 shadow-lg dark:shadow-black/25"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <CheckCircle className="w-5 h-5" />
                              <span>Aplicar</span>
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
  
      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Results Header */}
        <motion.div 
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center 
                   space-y-4 sm:space-y-0 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full shadow-lg">
              <TypeIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              {filteredProperties.length}{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-400 dark:to-blue-400">
                {typeMap[type]?.label || 'Propiedades'} Encontradas
              </span>
            </h2>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">
              {filters.operacion === 'alquiler' ? 'En Alquiler' : 'En Venta'}
            </span>
            {totalPages > 0 && (
              <span className="text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/50 px-3 py-1 rounded-full text-sm border border-gray-200 dark:border-gray-700">
                Página {currentPage} de {totalPages}
              </span>
            )}
          </div>
        </motion.div>
  
        {/* Properties Grid */}
        {filteredProperties.length === 0 ? (
          <NoResults />
        ) : (
          <>
            {/* Grid modificado para mostrar exactamente 3 columnas en layout grande */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {currentProperties.map((property) => (
                <motion.div
                  key={property.id}
                  variants={cardAnimation}
                  whileHover="hover"
                  className="h-full"
                >
                  <PropertyCard property={property} />
                </motion.div>
              ))}
            </motion.div>
  
            {/* Paginación mejorada */}
            {totalPages > 1 && (
              <motion.div 
                className="mt-16 flex flex-wrap justify-center items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {/* Botón para primera página */}
                {currentPage > 3 && totalPages > 5 && (
                  <PaginationButton
                    onClick={() => setCurrentPage(1)}
                    active={false}
                  >
                    1
                  </PaginationButton>
                )}
                
                {/* Ellipsis para indicar páginas omitidas al inicio */}
                {currentPage > 3 && totalPages > 5 && (
                  <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                )}
                
                {/* Botón anterior */}
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  active={false}
                >
                  <ChevronLeft className="w-5 h-5" />
                </PaginationButton>
                
                {/* Números de página */}
                {getPageNumbers().map(number => (
                  <PaginationButton
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    active={currentPage === number}
                  >
                    {number}
                  </PaginationButton>
                ))}
                
                {/* Ellipsis para indicar páginas omitidas al final */}
                {currentPage < totalPages - 2 && totalPages > 5 && (
                  <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                )}
                
                {/* Botón para última página */}
                {currentPage < totalPages - 2 && totalPages > 5 && (
                  <PaginationButton
                    onClick={() => setCurrentPage(totalPages)}
                    active={false}
                  >
                    {totalPages}
                  </PaginationButton>
                )}
                
                {/* Botón siguiente */}
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  active={false}
                >
                  <ChevronRight className="w-5 h-5" />
                </PaginationButton>
              </motion.div>
            )}
          </>
        )}
        
        {/* Información adicional sobre la paginación */}
        {filteredProperties.length > 0 && (
          <motion.div 
            className="mt-6 text-center text-gray-500 dark:text-gray-400 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            Mostrando {indexOfFirstProperty + 1} - {Math.min(indexOfLastProperty, filteredProperties.length)} de {filteredProperties.length} propiedades
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PropertyTypePage;